import YAML from 'yaml';
import { MissionError } from './missions.js';
/**
 * The second language.
 *
 * A mission is authored once, in English, in `mission.yaml`. Its Arabic lives
 * beside it in `mission.ar.yaml` as a *sidecar*: the same prose in the same
 * shape, and none of the machinery. No `check:`, no `with:`, no `level:`, no
 * seed. There is exactly one grader per level and it is not translated — a
 * translation that could change what passes would be a second definition of
 * the level, and the two would drift on the first day somebody edited one.
 *
 * So the sidecar is strings, and this module is the two operations that use
 * them: pull the English strings off a mission (`missionText`), and push a
 * translated set back on (`applyMissionText`). Everything in between — the
 * build step that ships them, the client that swaps them at runtime — is one
 * of those two calls.
 *
 * `applyMissionText` mutates. That is deliberate and it is the reason this
 * works at all: the browser holds one `Map` of missions that the map screen,
 * the session manager and the grader all read from (web/src/engine/runtime.
 * ts), and a language switch has to reach every one of them. Replacing map
 * entries would leave a running session pointing at the mission it was
 * started with, so an objective would still fail in English half a second
 * after the interface turned Arabic. Writing the strings onto the object each
 * of them already holds cannot miss one.
 *
 * See docs/i18n.md for what may be translated and what may never be.
 */
/** The languages the product is written in. English is the source. */
export const LOCALES = ['en', 'ar', 'de', 'ja'];
/**
 * Languages that exist and are not offered yet.
 *
 * A language is added to `LOCALES` on the day its interface strings are
 * written, because `typeof en` can only hold a translation to completeness if
 * the type knows the language is there. Its levels arrive a chapter at a time
 * after that, and until they have, a reader who got it from their browser's
 * settings would land on a German interface around English levels without
 * having asked for either.
 *
 * So a preview language is reachable only by asking for it by name — `?lang=de`
 * — and is never guessed from the browser (web/src/lib/locale.ts). Taking a
 * language off this list is the launch.
 *
 * German left it on 2026-09-29, launched with its levels part-written (the
 * owner's call: what is not translated yet is read in English). See
 * `PARTIAL_LOCALES` below for what that means to the tests. Japanese joined
 * the same day, interface first, and left it on 2026-09-30 with chapter 1
 * written, on the same call (docs/i18n.md §10).
 */
export const PREVIEW_LOCALES = [];
/**
 * Languages that are offered, or about to be, while some of their levels are
 * still English.
 *
 * Every string falls back to English on its own — a level with no sidecar is
 * read in English, a sidecar with a gap shows English in the gap — so a
 * partial language works; it is only incomplete. The tests that demand a
 * sidecar for every level skip a partial language (server/test/i18n.test.ts);
 * the tests that check a sidecar which exists do not, so every German level
 * that ships is held to every rule an Arabic one is. A language leaves this
 * list on the day its last level is translated.
 */
// Arabic joined on 2026-10-29: the owner put translation on hold ("English
// only until we get our product right"), and the 32 world merges shipped
// without Arabic rather than with stale Arabic for levels that had changed.
export const PARTIAL_LOCALES = ['ar', 'de', 'ja'];
/** The direction each language is set in. */
export const LOCALE_DIR = { en: 'ltr', ar: 'rtl', de: 'ltr', ja: 'ltr' };
export function isLocale(value) {
    return typeof value === 'string' && LOCALES.includes(value);
}
/**
 * The subset of a `MissionText` a locked stub is allowed to carry.
 *
 * The English catalogue withholds a paid level's brief, objectives, hints and
 * warm-up (scripts/build-missions.mjs, `lockedMission`), and the Arabic one
 * withholds exactly the same things. A translated level is still a level: a
 * shop window that happened to be translated would be the paid half, given
 * away in the other language.
 */
export function lockedText(t) {
    const out = {};
    if (t.title !== undefined)
        out.title = t.title;
    if (t.module !== undefined)
        out.module = t.module;
    return out;
}
/** The English strings, lifted off a mission so a translation can be checked against them. */
export function missionText(m) {
    // A locked stub is not a level. The catalogue withholds its brief,
    // constraints, objectives, hints and warm-up, so there is nothing here to
    // lift but the two strings the card on the map is made of, and reading it as
    // a level throws on the first spread below.
    //
    // That throw was real and it cost the whole feature. The one place a stub
    // ever meets this function is the browser — `keepEnglish` in
    // web/src/engine/runtime.ts, capturing the English before an overlay goes
    // over it — and every mission loaded from disk is whole, so no test on the
    // server side could see it. `applyLocale` walks the catalogue in order, hit
    // the first stub, and rejected; the `.then` that re-reads the list for React
    // never ran. The result was an Arabic interface with fifty English level
    // titles in it, in production, for anyone reading the course in Arabic.
    //
    // `lockedText` is the rule for what a stub may carry, stated once, a dozen
    // lines above. This is the caller it was written for.
    if (m.locked)
        return lockedText({ title: m.title, module: m.module });
    const out = {
        title: m.title,
        module: m.module,
        brief: m.brief,
        constraints: [...m.constraints],
        objectives: m.objectives.map((o) => {
            // `null` rather than omitted when there is no `fail`, so that restoring
            // the English removes one the translation added. See `ObjectiveText.fail`.
            const obj = { id: o.id, title: o.title, fail: o.fail ?? null };
            if (o.diagnostics)
                obj.diagnostics = o.diagnostics.map((d) => ({ message: d.message }));
            // The sub-checks' `detail`, if this objective is an `any_of`/`all_of`.
            //
            // Easy to leave out, and the omission would be invisible until somebody
            // switched language twice: this function is how the client captures the
            // English before an overlay is written over it (`applyLocale` in
            // web/src/engine/runtime.ts), so a field that is applied but not
            // captured is a field that never comes back. Everything
            // `applyMissionText` can write, this has to be able to read.
            const subs = o.with?.checks;
            if (Array.isArray(subs)) {
                obj.checks = subs.map((c) => ({ detail: typeof c?.with?.detail === 'string' ? c.with.detail : null }));
            }
            return obj;
        }),
        hints: m.hints.map((h) => ({ level: h.level, title: h.title, text: h.text })),
        reflection: m.reflection,
    };
    // The mentor's words, captured for the same reason everything else here is:
    // this is what the client holds onto before an overlay is written over the
    // mission (`applyLocale`, web/src/engine/runtime.ts), so a field applied but
    // not captured is a field that never comes back. `look` is not among them —
    // it is machine text and is never translated, so there is nothing to restore.
    if (m.situations && m.situations.length > 0) {
        out.situations = m.situations.map((s) => {
            // `ask ?? null` so that restoring the source removes a question a
            // translation added. See `SituationText.ask`.
            const sit = { id: s.id, say: s.say, ask: s.ask ?? null };
            if (s.choices) {
                sit.choices = s.choices.map((c) => ({ text: c.text, reply: c.reply, ...(c.right === true ? { right: true } : {}) }));
            }
            return sit;
        });
    }
    if (m.tutorial) {
        const t = {
            cards: m.tutorial.cards.map((c) => {
                // `label ?? null` so that restoring the source removes one a
                // translation added — the same rule, and the same reason, as `fail`.
                const card = { tool: c.tool, label: c.label ?? null, title: c.title };
                if (c.body !== undefined)
                    card.body = c.body;
                if (c.keys)
                    card.keys = c.keys.map((k) => ({ key: k.key, title: k.title }));
                if (c.try && (c.try.note !== undefined || c.try.nudge !== undefined)) {
                    card.try = {};
                    if (c.try.note !== undefined)
                        card.try.note = c.try.note;
                    if (c.try.nudge !== undefined)
                        card.try.nudge = c.try.nudge;
                }
                return card;
            }),
        };
        if (m.tutorial.title !== undefined)
            t.title = m.tutorial.title;
        if (m.tutorial.lede !== undefined)
            t.lede = m.tutorial.lede;
        out.tutorial = t;
    }
    if (m.irisScript) {
        out.iris_script = m.irisScript.map((b) => ({
            id: b.id,
            say: b.say,
            ...(b.replies ? { replies: b.replies.map((r) => r.say) } : {}),
        }));
    }
    return out;
}
/**
 * The keys a sidecar situation may not carry.
 *
 * Four of them are the grader's — `check` and `with` are the recognizer, `for`
 * names objectives by id, `free_through` unlocks rungs — and this file's whole
 * argument is that there is one grader per level and it is not translated.
 * `look` is here for the other reason given on {@link SituationText}: it is a
 * command shape, it is machine text, and it is the same in both languages.
 */
const GRADING_KEYS = ['check', 'with', 'look', 'for', 'free_through'];
/** A string field if it is a usable one, and `undefined` for anything else. */
function text(value) {
    return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}
function assign(target, key, value) {
    if (value !== undefined)
        target[key] = value;
}
/**
 * Read a `mission.ar.yaml`.
 *
 * Deliberately forgiving about what is absent and strict about what is wrong.
 * A missing field is an untranslated field, which is a normal state for a
 * course being translated one level at a time; a `hints:` that is a mapping
 * rather than a list is a mistake, and it is named here rather than dropped,
 * because a silently ignored translation is one nobody notices is missing.
 */
export function parseMissionText(yamlText, id) {
    let raw;
    try {
        raw = YAML.parse(yamlText);
    }
    catch (err) {
        throw new MissionError(id, `translation is not valid YAML (${err.message})`);
    }
    if (raw === null || raw === undefined)
        return {};
    if (typeof raw !== 'object' || Array.isArray(raw))
        throw new MissionError(id, 'translation must be a mapping');
    const r = raw;
    // A checkpoint's Iris block is instructions to the model, not words for
    // the learner, and Iris answers in the learner's language whatever it was
    // told in (docs/iris.md). So there is nothing in it to translate, and a
    // sidecar carrying one is somebody translating a directive that the build
    // would otherwise drop without a word.
    for (const key of ['iris', 'irisBudget']) {
        if (r[key] !== undefined) {
            throw new MissionError(id, `translation carries "${key}", which is instructions to the model and is never translated; it lives in mission.yaml only`);
        }
    }
    const out = {};
    assign(out, 'title', text(r.title));
    assign(out, 'module', text(r.module));
    assign(out, 'brief', text(r.brief));
    assign(out, 'reflection', text(r.reflection));
    if (r.constraints !== undefined) {
        if (!Array.isArray(r.constraints))
            throw new MissionError(id, 'translation "constraints" must be a list');
        out.constraints = r.constraints.map((c) => String(c));
    }
    if (r.objectives !== undefined) {
        if (!Array.isArray(r.objectives))
            throw new MissionError(id, 'translation "objectives" must be a list');
        out.objectives = r.objectives.map((o, i) => {
            if (typeof o !== 'object' || o === null)
                throw new MissionError(id, `translation objectives[${i}] must be a mapping`);
            const objId = text(o.id);
            if (!objId)
                throw new MissionError(id, `translation objectives[${i}] needs the English objective's "id"`);
            const obj = { id: objId };
            assign(obj, 'title', text(o.title));
            assign(obj, 'fail', text(o.fail));
            if (o.diagnostics !== undefined) {
                if (!Array.isArray(o.diagnostics))
                    throw new MissionError(id, `translation objective "${objId}" diagnostics must be a list`);
                obj.diagnostics = o.diagnostics.map((d) => ({ message: text(d?.message) }));
            }
            if (o.checks !== undefined) {
                if (!Array.isArray(o.checks))
                    throw new MissionError(id, `translation objective "${objId}" checks must be a list`);
                obj.checks = o.checks.map((c) => ({ detail: text(c?.detail) }));
            }
            return obj;
        });
    }
    if (r.situations !== undefined) {
        if (!Array.isArray(r.situations))
            throw new MissionError(id, 'translation "situations" must be a list');
        out.situations = r.situations.map((s, i) => {
            if (typeof s !== 'object' || s === null || Array.isArray(s)) {
                throw new MissionError(id, `translation situations[${i}] must be a mapping`);
            }
            const sitId = text(s.id);
            if (!sitId)
                throw new MissionError(id, `translation situations[${i}] needs the English situation's "id"`);
            // Named and refused, not dropped. A grading field written into a sidecar
            // is somebody who believes they are translating the recognizer, and the
            // silent version of this rule leaves them believing it.
            for (const key of GRADING_KEYS) {
                if (s[key] !== undefined) {
                    throw new MissionError(id, `translation situation "${sitId}" carries "${key}", which is ${key === 'look' ? 'machine text and stays as authored in both languages' : 'the grader’s and is never translated'}`);
                }
            }
            const sit = { id: sitId };
            assign(sit, 'say', text(s.say));
            assign(sit, 'ask', text(s.ask));
            if (s.choices !== undefined) {
                if (!Array.isArray(s.choices))
                    throw new MissionError(id, `translation situation "${sitId}" choices must be a list`);
                sit.choices = s.choices.map((c, j) => {
                    if (typeof c !== 'object' || c === null || Array.isArray(c)) {
                        throw new MissionError(id, `translation situation "${sitId}" choices[${j}] must be a mapping`);
                    }
                    const choice = {};
                    assign(choice, 'text', text(c.text));
                    assign(choice, 'reply', text(c.reply));
                    // The anchor, read and never applied. See `SituationChoiceText.right`.
                    if (c.right !== undefined) {
                        if (typeof c.right !== 'boolean') {
                            throw new MissionError(id, `translation situation "${sitId}" choices[${j}] "right" must be true or absent`);
                        }
                        choice.right = c.right;
                    }
                    return choice;
                });
            }
            return sit;
        });
    }
    if (r.hints !== undefined) {
        if (!Array.isArray(r.hints))
            throw new MissionError(id, 'translation "hints" must be a list');
        out.hints = r.hints.map((h, i) => {
            if (typeof h !== 'object' || h === null)
                throw new MissionError(id, `translation hints[${i}] must be a mapping`);
            // Defaulted to position rather than required, so a translator who
            // copies the ladder in order does not have to renumber it by hand.
            const level = typeof h.level === 'number' ? h.level : i + 1;
            const hint = { level };
            assign(hint, 'title', text(h.title));
            assign(hint, 'text', text(h.text));
            return hint;
        });
    }
    if (r.tutorial !== undefined) {
        if (typeof r.tutorial !== 'object' || r.tutorial === null || Array.isArray(r.tutorial)) {
            throw new MissionError(id, 'translation "tutorial" must be a mapping');
        }
        const t = r.tutorial;
        const tut = {};
        assign(tut, 'title', text(t.title));
        assign(tut, 'lede', text(t.lede));
        if (t.cards !== undefined) {
            if (!Array.isArray(t.cards))
                throw new MissionError(id, 'translation "tutorial.cards" must be a list');
            tut.cards = t.cards.map((c, i) => {
                if (typeof c !== 'object' || c === null)
                    throw new MissionError(id, `translation tutorial.cards[${i}] must be a mapping`);
                const tool = text(c.tool);
                if (!tool)
                    throw new MissionError(id, `translation tutorial.cards[${i}] needs the English card's "tool"`);
                const card = { tool };
                assign(card, 'label', text(c.label));
                assign(card, 'title', text(c.title));
                assign(card, 'body', text(c.body));
                if (c.keys !== undefined) {
                    if (!Array.isArray(c.keys))
                        throw new MissionError(id, `translation tutorial.cards[${i}] keys must be a list`);
                    card.keys = c.keys.map((k) => ({
                        key: String(k?.key ?? ''),
                        title: text(k?.title) ?? '',
                    }));
                }
                if (c.try !== undefined) {
                    if (typeof c.try !== 'object' || c.try === null)
                        throw new MissionError(id, `translation tutorial.cards[${i}] try must be a mapping`);
                    const tr = c.try;
                    const out = {};
                    assign(out, 'note', text(tr.note));
                    assign(out, 'nudge', text(tr.nudge));
                    card.try = out;
                }
                return card;
            });
        }
        out.tutorial = tut;
    }
    if (r.iris_script !== undefined) {
        if (!Array.isArray(r.iris_script))
            throw new MissionError(id, 'translation "iris_script" must be a list');
        out.iris_script = r.iris_script.map((b, i) => {
            if (typeof b !== 'object' || b === null || Array.isArray(b))
                throw new MissionError(id, `translation iris_script[${i}] must be a mapping`);
            const beatId = text(b.id);
            if (!beatId)
                throw new MissionError(id, `translation iris_script[${i}] needs the English beat's "id"`);
            // Words only. A command, a target, a planted mark, a speaker, a cost
            // or shown machine text in a sidecar is somebody translating the
            // script's behaviour, and the build would otherwise drop it without a
            // word.
            for (const key of ['run', 'check', 'next', 'rejected', 'caught_by', 'agent', 'show', 'tokens', 'ask']) {
                if (b[key] !== undefined)
                    throw new MissionError(id, `translation iris_script beat "${beatId}" carries "${key}", which is never translated`);
            }
            const beat = { id: beatId };
            assign(beat, 'say', text(b.say));
            if (b.replies !== undefined) {
                if (!Array.isArray(b.replies))
                    throw new MissionError(id, `translation iris_script beat "${beatId}" replies must be a list`);
                beat.replies = b.replies.map((r) => String(r ?? ''));
            }
            return beat;
        });
    }
    return out;
}
/**
 * Everything wrong with a translation, as sentences, rather than the first
 * thing wrong with it as a throw.
 *
 * A translator fixing a sidecar wants the whole list; a build that stops at
 * the first mismatched objective id makes them run it fifty times. Nothing
 * here is fatal on its own — `applyMissionText` ignores what it cannot place
 * — so the caller decides whether a given problem fails a build or prints a
 * warning. `npm test` treats all of them as failures; the dev build does not.
 */
export function checkMissionText(m, t, where = 'ar') {
    const problems = [];
    const say = (s) => problems.push(`${m.id} (${where}): ${s}`);
    if (t.constraints && t.constraints.length !== m.constraints.length) {
        say(`${t.constraints.length} constraints translated, ${m.constraints.length} in the English`);
    }
    if (t.objectives) {
        const known = new Map(m.objectives.map((o) => [o.id, o]));
        const seen = new Set();
        for (const o of t.objectives) {
            const english = known.get(o.id);
            if (!english) {
                say(`objective "${o.id}" is not an objective of this level`);
                continue;
            }
            if (seen.has(o.id))
                say(`objective "${o.id}" is translated twice`);
            seen.add(o.id);
            if (o.diagnostics && o.diagnostics.length !== (english.diagnostics?.length ?? 0)) {
                say(`objective "${o.id}" has ${o.diagnostics.length} diagnostics, the English has ${english.diagnostics?.length ?? 0}`);
            }
            if (o.checks) {
                const subs = english.with?.checks;
                const have = Array.isArray(subs) ? subs.length : 0;
                if (o.checks.length !== have)
                    say(`objective "${o.id}" translates ${o.checks.length} sub-checks, the English has ${have}`);
            }
        }
        for (const o of m.objectives)
            if (!seen.has(o.id))
                say(`objective "${o.id}" is not translated`);
    }
    if (t.situations) {
        const known = new Map((m.situations ?? []).map((s) => [s.id, s]));
        const seen = new Set();
        for (const s of t.situations) {
            const english = known.get(s.id);
            if (!english) {
                say(`situation "${s.id}" is not a situation of this level`);
                continue;
            }
            if (seen.has(s.id))
                say(`situation "${s.id}" is translated twice`);
            seen.add(s.id);
            if (english.ask !== undefined && (s.ask === undefined || s.ask === null))
                say(`situation "${s.id}" question is not translated`);
            // The one mismatch here that grades somebody wrongly rather than
            // leaving them reading English. See `SituationChoiceText`.
            const have = english.choices?.length ?? 0;
            const got = s.choices?.length ?? 0;
            if (s.choices && got !== have) {
                say(`situation "${s.id}" translates ${got} choices, the English has ${have}; "right:" is matched by position`);
            }
            else if (s.choices) {
                // The anchor, compared. A sidecar that marks a different choice as the
                // right one has been reordered, and reordering is the one edit that
                // cannot be seen by reading either file on its own.
                const mine = s.choices.findIndex((c) => c.right === true);
                const theirs = english.choices?.findIndex((c) => c.right === true) ?? -1;
                if (mine !== -1 && mine !== theirs) {
                    say(`situation "${s.id}" marks choice ${mine + 1} as the right one, the English marks ${theirs + 1}`);
                }
            }
        }
        for (const s of m.situations ?? [])
            if (!seen.has(s.id))
                say(`situation "${s.id}" is not translated`);
    }
    else if ((m.situations?.length ?? 0) > 0 && Object.keys(t).length > 0) {
        say(`${m.situations.length} situation(s) and no counterpart at all`);
    }
    if (t.hints) {
        const rungs = new Set(m.hints.map((h) => h.level));
        const seen = new Set();
        for (const h of t.hints) {
            if (!rungs.has(h.level))
                say(`hint ${h.level} is not a rung of this level's ladder`);
            if (seen.has(h.level))
                say(`hint ${h.level} is translated twice`);
            seen.add(h.level);
        }
        for (const h of m.hints)
            if (!seen.has(h.level))
                say(`hint ${h.level} is not translated`);
    }
    if (t.tutorial?.cards) {
        const cards = m.tutorial?.cards ?? [];
        if (!m.tutorial) {
            say('a warm-up is translated, but the level has none');
        }
        else if (t.tutorial.cards.length !== cards.length) {
            say(`${t.tutorial.cards.length} warm-up cards translated, ${cards.length} in the English`);
        }
        else {
            t.tutorial.cards.forEach((c, i) => {
                const english = cards[i];
                // The anchor. See `TutorialCardText.tool`.
                if (c.tool !== english.tool)
                    say(`warm-up card ${i + 1} is "${c.tool}", the English card there is "${english.tool}"`);
                if (c.keys && c.keys.length !== (english.keys?.length ?? 0)) {
                    say(`warm-up card ${i + 1} has ${c.keys.length} keys, the English has ${english.keys?.length ?? 0}`);
                }
            });
        }
    }
    else if (m.tutorial && t.tutorial === undefined && Object.keys(t).length > 0) {
        say('the warm-up is not translated');
    }
    if (t.iris_script) {
        const beats = new Map((m.irisScript ?? []).map((b) => [b.id, b]));
        const seen = new Set();
        for (const b of t.iris_script) {
            const english = beats.get(b.id);
            if (!english) {
                say(`Iris's beat "${b.id}" is not a beat of this level`);
                continue;
            }
            seen.add(b.id);
            if (b.replies && b.replies.length !== (english.replies?.length ?? 0)) {
                say(`Iris's beat "${b.id}" has ${b.replies.length} replies, the English has ${english.replies?.length ?? 0}`);
            }
        }
        for (const b of m.irisScript ?? [])
            if (!seen.has(b.id))
                say(`Iris's beat "${b.id}" is not translated`);
    }
    else if ((m.irisScript?.length ?? 0) > 0 && Object.keys(t).length > 0) {
        say("Iris's script is not translated");
    }
    return problems;
}
/**
 * Write a translated set of strings onto a mission, in place.
 *
 * Only what is present is written, so this is also how a language is turned
 * off: hand it the English `missionText` and every string goes back. Anything
 * that cannot be placed — an objective id the level does not have, a hint on
 * a rung that is not there — is skipped rather than invented, because the
 * alternative is a level that grows an objective in one language.
 *
 * Nothing here touches a `check`, a `with`, a `run`, an `accept` or an
 * `output`. That is the property this file exists to keep: the two languages
 * are the same level.
 */
export function applyMissionText(m, t) {
    assign(m, 'title', t.title);
    assign(m, 'module', t.module);
    assign(m, 'brief', t.brief);
    assign(m, 'reflection', t.reflection);
    if (t.constraints && t.constraints.length === m.constraints.length)
        m.constraints = [...t.constraints];
    if (t.objectives) {
        const by = new Map(t.objectives.map((o) => [o.id, o]));
        for (const o of m.objectives) {
            const tr = by.get(o.id);
            if (!tr)
                continue;
            assign(o, 'title', tr.title);
            // `null` means the source has none, so remove the one a translation
            // added; `undefined` means untranslated, so leave whatever is there.
            if (tr.fail === null)
                delete o.fail;
            else
                assign(o, 'fail', tr.fail);
            if (tr.diagnostics && o.diagnostics && tr.diagnostics.length === o.diagnostics.length) {
                o.diagnostics.forEach((d, i) => assign(d, 'message', tr.diagnostics[i].message));
            }
            // The sub-checks of an `any_of`/`all_of`, by position, and only their
            // `detail`. See `ObjectiveText.checks`.
            const subs = o.with?.checks;
            if (tr.checks && Array.isArray(subs) && tr.checks.length === subs.length) {
                subs.forEach((sub, i) => {
                    const detail = tr.checks[i].detail;
                    if (detail === undefined)
                        return;
                    if (!sub.with || typeof sub.with !== 'object')
                        sub.with = {};
                    if (detail === null)
                        delete sub.with.detail;
                    else
                        sub.with.detail = detail;
                });
            }
        }
    }
    if (t.situations && m.situations) {
        const by = new Map(t.situations.map((s) => [s.id, s]));
        for (const s of m.situations) {
            const tr = by.get(s.id);
            if (!tr)
                continue;
            assign(s, 'say', tr.say);
            // `null` means the English asks nothing, so remove a question a
            // translation added; `undefined` means untranslated, so leave it.
            if (tr.ask === null)
                delete s.ask;
            else
                assign(s, 'ask', tr.ask ?? undefined);
            // All of them or none. A partial overlay here would leave one choice in
            // English beside three in Arabic, and a *misaligned* one would put the
            // wrong reply under the right answer — which is why the length has to
            // agree before a single string is written.
            if (tr.choices && s.choices && tr.choices.length === s.choices.length) {
                s.choices.forEach((c, i) => {
                    const from = tr.choices[i];
                    assign(c, 'text', from.text);
                    assign(c, 'reply', from.reply);
                });
            }
            // `check`, `with`, `look`, `for` and `free_through` are untouched, and
            // the parser has already refused a sidecar that tried to carry one.
        }
    }
    if (t.hints) {
        const by = new Map(t.hints.map((h) => [h.level, h]));
        for (const h of m.hints) {
            const tr = by.get(h.level);
            if (!tr)
                continue;
            assign(h, 'title', tr.title);
            assign(h, 'text', tr.text);
        }
    }
    if (t.tutorial && m.tutorial) {
        assign(m.tutorial, 'title', t.tutorial.title);
        assign(m.tutorial, 'lede', t.tutorial.lede);
        const cards = t.tutorial.cards;
        if (cards && cards.length === m.tutorial.cards.length) {
            m.tutorial.cards.forEach((card, i) => {
                const tr = cards[i];
                if (tr.tool !== card.tool)
                    return;
                assign(card, 'title', tr.title);
                // Onto `label`, never over `tool`: `tool` is the anchor this very
                // comparison depends on, and a card whose anchor had been translated
                // could not be matched a second time — which is what switching back
                // to the source language is.
                if (tr.label === null)
                    delete card.label;
                else
                    assign(card, 'label', tr.label);
                assign(card, 'body', tr.body);
                if (tr.keys && card.keys && tr.keys.length === card.keys.length) {
                    card.keys.forEach((k, j) => {
                        const t = tr.keys[j].title;
                        if (t)
                            k.title = t;
                    });
                }
                if (tr.try && card.try) {
                    assign(card.try, 'note', tr.try.note);
                    assign(card.try, 'nudge', tr.try.nudge);
                }
            });
        }
    }
    if (t.iris_script && m.irisScript) {
        const by = new Map(t.iris_script.map((b) => [b.id, b]));
        for (const beat of m.irisScript) {
            const tr = by.get(beat.id);
            if (!tr)
                continue;
            assign(beat, 'say', tr.say);
            // All of them or none, for the reason a situation's choices are.
            if (tr.replies && beat.replies && tr.replies.length === beat.replies.length) {
                beat.replies.forEach((r, i) => {
                    const said = tr.replies[i];
                    if (said)
                        r.say = said;
                });
            }
        }
    }
}
/**
 * A mission with a translation applied, leaving the original alone.
 *
 * The build step's version of `applyMissionText`: it emits both languages
 * from one load, so it cannot be the one that mutates. The copy is deep
 * exactly as far as the strings reach and shallow everywhere else — the seed
 * script and the checks are shared, because they are the same checks.
 */
export function localizedMission(m, t) {
    const copy = {
        ...m,
        constraints: [...m.constraints],
        objectives: m.objectives.map((o) => ({
            ...o,
            // `with` is copied only as deep as a translation can reach — the
            // sub-checks and their `with` — so that writing a translated `detail`
            // into the copy cannot reach back into the English mission.
            ...(o.with
                ? {
                    with: {
                        ...o.with,
                        ...(Array.isArray(o.with.checks)
                            ? {
                                checks: (o.with.checks ?? []).map((c) => ({
                                    ...c,
                                    ...(c.with ? { with: { ...c.with } } : {}),
                                })),
                            }
                            : {}),
                    },
                }
                : {}),
            ...(o.diagnostics ? { diagnostics: o.diagnostics.map((d) => ({ ...d })) } : {}),
        })),
        hints: m.hints.map((h) => ({ ...h })),
        // As deep as a translation reaches and no deeper: the situation and its
        // choices are copied because their prose is written into, while `with` is
        // shared because it is the same recognizer running the same check.
        ...(m.situations
            ? {
                situations: m.situations.map((s) => ({
                    ...s,
                    ...(s.choices ? { choices: s.choices.map((c) => ({ ...c })) } : {}),
                })),
            }
            : {}),
        ...(m.irisScript
            ? {
                irisScript: m.irisScript.map((b) => ({
                    ...b,
                    ...(b.replies ? { replies: b.replies.map((r) => ({ ...r })) } : {}),
                })),
            }
            : {}),
        ...(m.tutorial
            ? {
                tutorial: {
                    ...m.tutorial,
                    cards: m.tutorial.cards.map((c) => ({
                        ...c,
                        ...(c.keys ? { keys: c.keys.map((k) => ({ ...k })) } : {}),
                        ...(c.try ? { try: { ...c.try } } : {}),
                    })),
                },
            }
            : {}),
    };
    applyMissionText(copy, t);
    return copy;
}
//# sourceMappingURL=i18n.js.map